/**
 * The Hour Optimizer's read: a campaign's day as 24 hour buckets ranked on
 * the editor's LIVE formula (POST preview, zero writes). Formula and floor
 * are debounced together so a slider drag or a typed number asks once; slider
 * rows without a metric are dropped from the request; the previous answer
 * stays on screen while the next one loads, so the chart re-renders instead
 * of flashing empty. `pending` = an edit still waiting out the debounce.
 * The hour rules (Phase 19) ride along: each bucket comes back with its hits.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { cleanHourFormula, type DropGenHourFormula, type DropGenHourProfile, type DropGenRule } from '@trafficpulse/shared';

import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { api } from '@/services/api';

import { dropGeneratorKeys } from '../useDropGenerator';

export interface HourProfileArgs {
  countryKey: string;
  campaignWiserId: number | null;
  formula: DropGenHourFormula;
  /** Phase 19 — the hour rules, judged on every bucket (their hits come back); Phase 22 — "Not enough data" among them. */
  rules?: DropGenRule[];
}

export function useHourProfile(args: HourProfileArgs) {
  const { countryKey, campaignWiserId } = args;
  // Debounce ONE stable string: an object would be new on every render and
  // restart the debounce forever (a re-render every 400 ms while open).
  const liveKey = JSON.stringify({ formula: cleanHourFormula(args.formula), rules: args.rules ?? [] });
  const bodyKey = useDebouncedValue(liveKey, 400);
  const body = useMemo(() => JSON.parse(bodyKey) as { formula: DropGenHourFormula; rules: DropGenRule[] }, [bodyKey]);
  const query = useQuery({
    queryKey: [...dropGeneratorKeys.all, 'hour-profile', countryKey, campaignWiserId, bodyKey] as const,
    queryFn: async () => {
      const res = await api.post<{ data: DropGenHourProfile }>(`/drop-generator/hour-profile/${countryKey}/${campaignWiserId}/preview`, body);
      return res.data.data;
    },
    enabled: Boolean(countryKey && campaignWiserId && body.formula.groups.length > 0),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
  });
  return { ...query, pending: liveKey !== bodyKey };
}
